import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const FlashSales = () => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 12,
    minutes: 32,
    seconds: 1
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        const { hours, minutes, seconds } = prev;
        if (seconds > 0) {
          return { ...prev, seconds: seconds - 1 };
        } else if (minutes > 0) {
          return { hours, minutes: minutes - 1, seconds: 59 };
        } else if (hours > 0) {
          return { hours: hours - 1, minutes: 59, seconds: 59 };
        } else {
          return { hours: 12, minutes: 32, seconds: 1 }; // Reset
        }
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const flashProducts = [
    {
      name: 'Smart Water Valve',
      originalPrice: 'KSH 4,500',
      salePrice: 'KSH 2,999',
      discount: '-33%',
      image: '/images/products/Smart.Water.Valve.png',
      rating: 4.5
    },
    {
      name: 'LED Panel Light 2x4',
      originalPrice: 'KSH 12,000',
      salePrice: 'KSH 8,950',
      discount: '-25%',
      image: '/images/products/LED.Panel.Light.2x4.png',
      rating: 4.8
    },
    {
      name: 'Pipe Wrench Set',
      originalPrice: 'KSH 5,200',
      salePrice: 'KSH 3,575',
      discount: '-31%',
      image: '/images/products/Pipe.Wrench.Set.png',
      rating: 4.3
    },
    {
      name: 'Voltage Tester Pro',
      originalPrice: 'KSH 3,200',
      salePrice: 'KSH 2,450',
      discount: '-23%',
      image: '/images/products/Voltage.Tester.Pro.png',
      rating: 4.7
    }
  ];

  return (
    <section className="py-12 bg-white">
      <div className="container mx-auto px-4">
        {/* Flash Sales Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-gray-800">Flash Sales</h2>
            <div className="flex items-center space-x-2 bg-red-50 px-3 py-1 rounded-full">
              <span className="text-red-500 text-sm font-semibold">🔥</span>
              <span className="text-red-500 text-sm font-semibold">HOT DEALS</span>
            </div>
          </div>
          
          {/* Countdown Timer */}
          <div className="flex items-center space-x-2">
            <span className="text-gray-600 text-sm">Ends in:</span>
            <div className="flex space-x-1">
              {[
                { value: timeLeft.hours, label: 'Hrs' },
                { value: timeLeft.minutes, label: 'Min' },
                { value: timeLeft.seconds, label: 'Sec' }
              ].map((time, index) => (
                <div key={time.label} className="flex items-center">
                  <div className="bg-gray-800 text-white px-2 py-1 rounded text-sm font-mono">
                    {time.value.toString().padStart(2, '0')}
                  </div>
                  {index < 2 && <span className="mx-1 text-gray-400">:</span>}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Flash Sales Products */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {flashProducts.map((product, index) => (
            <motion.div
              key={product.name}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              {/* Discount Badge */}
              <div className="absolute top-3 left-3 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold z-10">
                {product.discount}
              </div>

              {/* Product Image */}
              <div className="h-48 bg-gray-100 flex items-center justify-center relative">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="h-40 w-40 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    // Show fallback content
                    const fallback = document.createElement('div');
                    fallback.className = 'text-center';
                    fallback.innerHTML = `
                      <div class="w-16 h-16 bg-orange-500 rounded-lg mx-auto mb-2 flex items-center justify-center">
                        <span class="text-white text-xl">⚡</span>
                      </div>
                      <span class="text-orange-600 text-sm font-medium">Flash Sale</span>
                    `;
                    e.target.parentNode.appendChild(fallback);
                  }}
                />
              </div>

              {/* Product Info */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-800 mb-2">{product.name}</h3>
                
                {/* Rating */}
                <div className="flex items-center space-x-1 mb-3">
                  <div className="flex text-yellow-400">
                    {'★'.repeat(Math.floor(product.rating))}
                    {'☆'.repeat(5 - Math.floor(product.rating))}
                  </div>
                  <span className="text-sm text-gray-500">({product.rating})</span>
                </div>

                {/* Pricing */}
                <div className="flex items-center space-x-2 mb-3">
                  <span className="text-xl font-bold text-gray-800">{product.salePrice}</span>
                  <span className="text-sm text-gray-500 line-through">{product.originalPrice}</span>
                </div>

                {/* Add to Cart Button */}
                <motion.button
                  className="w-full bg-orange-500 text-white py-2 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Add to Cart
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FlashSales;
